import type { IntlShape } from 'react-intl';

import {
  AiLabsEntryPointId,
  type EntrypointId,
  TrelloLabsEntryPointId,
} from '@trello/feedback-collector';

/**
 * A {@link Facet} represents an aspect of an AI feature that a user can give feedback on.
 */
export interface Facet {
  /**
   * A descriptive name for the facet.
   */
  readonly name: string;

  /**
   * An internationalized label for the facet.
   */
  readonly label: string;
}

/**
 * A {@link FeedbackFeature} represents an AI feature that feedback can be given for.
 * These are used in conjunction with the AI Labs feedback collector hook and component in order to display standardized
 * feedback UI for all AI Labs features.
 *
 * This class is abstract; it acts as a pseudo-enum with associated behavior.
 * Implementors are expected to extend it and assign a value to the {@link FeedbackFeatures} record, overriding
 * properties as needed to customize behavior.
 */
abstract class FeedbackFeature {
  /**
   * The key to associate with the AI Labs feature.
   * Defaults to 'global'.
   */
  public projectKey(): string {
    return 'global';
  }

  /**
   * The entrypoint to record for feedback.
   * Defaults to the {@link AiLabsEntryPointId}.
   */
  public entrypoint(): EntrypointId {
    return AiLabsEntryPointId;
  }

  /**
   * A list of facets to provide as options for stock feedback when the feature did not work as expected.
   * Defaults to an empty list.
   */
  public negativeFacets(intl: IntlShape): Facet[] {
    return [];
  }

  /**
   * Formats a localized message to show in the feedback collector dialog.
   * Defaults to the `templates.ai-labs.feedback-collector-modal-title` string.
   * @param intl An internationalization helper with `formatMessage`.
   */
  public dialogTitle(intl: IntlShape): string {
    return intl.formatMessage({
      id: 'templates.ai-labs.feedback-collector-modal-title',
      defaultMessage: 'Share your thoughts',
      description: 'Title for the detailed feedback modal',
    });
  }

  /**
   * Formats a localized message to show in the feedback collector flag, if used.
   * Defaults to the `templates.ai-labs.feedback-collector-popup-title` string.
   * @param intl An internationalization helper with `formatMessage`.
   */
  public flagTitle(intl: IntlShape): string {
    return intl.formatMessage({
      id: 'templates.ai-labs.feedback-collector-popup-title',
      defaultMessage: 'How did Rovo do?',
      description: 'Title for the feedback popup',
    });
  }

  /**
   * Formats an optional localized subtitle to show in the feedback collector flag.
   */
  public flagSubtitle(_intl: IntlShape): string | undefined {
    return undefined;
  }
}

const features = Object.freeze({
  aiBoardBuilder: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'board-builder';
    }

    public override entrypoint(): EntrypointId {
      /*
       * Route AI Board Builder feedback to the Trello Labs Jira project (TL)
       * instead of the shared AI Labs project (TAIL).
       */
      return TrelloLabsEntryPointId;
    }

    public override dialogTitle(intl: IntlShape): string {
      return intl.formatMessage({
        id: 'templates.ai_board_builder.feedback-modal-title',
        defaultMessage: 'Share your thoughts on the AI Board Builder feature',
        description:
          'Title for the detailed feedback modal for the AI Board Builder feature',
      });
    }
  })(),
  aiBoardBuilderWithCommandCenter: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'board-builder-m2';
    }

    public override entrypoint(): EntrypointId {
      /*
       * Route AI Board Builder feedback to the Trello Labs Jira project (TL)
       * instead of the shared AI Labs project (TAIL).
       */
      return TrelloLabsEntryPointId;
    }

    public override dialogTitle(intl: IntlShape): string {
      return intl.formatMessage({
        id: 'templates.ai_board_builder.feedback-modal-title',
        defaultMessage: 'Share your thoughts on the AI Board Builder feature',
        description:
          'Title for the detailed feedback modal for the AI Board Builder feature',
      });
    }
  })(),
  aiCardMerge: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'card-merge';
    }
  })(),
  aiCardSummary: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'card-summary';
    }
  })(),
  aiGenerateChecklist: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'generate-checklist';
    }
  })(),
  aiImageGeneration: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'image-generation';
    }
  })(),
  cardRecommendation: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'card-recommendation';
    }

    // TODO: i18n
    public override flagTitle(_intl: IntlShape): string {
      return 'Was this recommendation useful?';
    }

    // TODO: i18n
    public override flagSubtitle(_intl: IntlShape): string {
      return 'Help us improve.';
    }
  })(),
  smartScheduling: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'smart-schedule';
    }

    public override negativeFacets(intl: IntlShape): Facet[] {
      return [
        {
          name: 'duration',
          label: intl.formatMessage({
            id: 'templates.ai-labs.feedback-facet-duration',
            defaultMessage: 'Duration of event',
            description: 'Negative feedback reason option',
          }),
        },
        {
          name: 'time',
          label: intl.formatMessage({
            id: 'templates.ai-labs.feedback-facet-time',
            defaultMessage: 'Time slot',
            description: 'Negative feedback reason option',
          }),
        },
        {
          name: 'date',
          label: intl.formatMessage({
            id: 'templates.ai-labs.feedback-facet-date',
            defaultMessage: 'Date of event',
            description: 'Negative feedback reason option',
          }),
        },
        {
          name: 'priority',
          label: intl.formatMessage({
            id: 'templates.ai-labs.feedback-facet-priority',
            defaultMessage: 'Priority of events',
            description: 'Negative feedback reason option',
          }),
        },
        {
          name: 'tooLong',
          label: intl.formatMessage({
            id: 'templates.ai-labs.feedback-facet-too-long',
            defaultMessage: 'LLM took too long',
            description: 'Negative feedback reason option',
          }),
        },
      ];
    }
  })(),
  voiceCapture: new (class extends FeedbackFeature {
    public override projectKey(): string {
      return 'voice-capture';
    }

    public override flagTitle(intl: IntlShape): string {
      return intl.formatMessage({
        id: 'templates.voice_capture.feedback-prompt-title',
        defaultMessage: 'How did Voice Capture do?',
        description: 'Title asking users for feedback on Voice Capture',
      });
    }

    public override flagSubtitle(intl: IntlShape): string {
      return intl.formatMessage({
        id: 'templates.voice_capture.feedback-prompt-subtitle',
        defaultMessage: 'Help us improve AI accuracy.',
        description:
          'Subtitle encouraging users to share Voice Capture feedback',
      });
    }
  })(),
});

/**
 * The names of AI Labs features which can have feedback submitted.
 */
export type FeatureNames = keyof typeof features;

/**
 * A pseudo-enum record of feedback-enabled AI Labs features.
 */
export const FeedbackFeatures = features as Record<
  FeatureNames,
  FeedbackFeature
>;
